import { captureReadingContact } from "@/lib/api/chat";
import { trackEvent } from "@/lib/analytics-events";
import { CAPTURE_POINTS } from "@/lib/contact-capture-points";
import { normalizeIndianPhone } from "@/lib/phone-india";

/**
 * Persist the chat-entry form's phone (required) + email (optional) via the
 * EXISTING consent-correct backend (captureReadingContact → POST
 * /readings/{id}/contact). The backend already accepts phone-only, so no
 * backend change is needed — phone alone grants WhatsApp opt-in and counts for
 * reading_has_contact.
 *
 * Called AFTER the kundali/reading is created (so a real reading_id exists) so
 * the contact lands BEFORE message 1 on the chat surface. This lets us later
 * revert the chat soft-wall without losing contacts.
 *
 * Soft-fail (rule 41) + idempotent-friendly: a capture hiccup must never block
 * or error the chat the seeker is waiting on. No-op when there's no reading_id
 * or no valid phone (so it never throws on the value path).
 */
export async function persistFormContact(
  readingId: string | undefined | null,
  phone: string,
  email: string
): Promise<void> {
  const normalizedPhone = readingId ? normalizeIndianPhone(phone) : null;
  if (!readingId || !normalizedPhone) return;
  const trimmedEmail = email.trim();
  try {
    await captureReadingContact(readingId, {
      phone: normalizedPhone,
      email: trimmedEmail || undefined,
      // Providing the number on the chat-entry form is a transactional
      // WhatsApp-delivery action; the backend's register_value_moment_optin
      // grants the WhatsApp consent on phone.
      consent_marketing: true,
    });
    trackEvent("contact_captured", {
      reading_id: readingId,
      type: trimmedEmail ? "phone_email" : "phone",
      placement: "kundali-form-contact",
      capture_point: CAPTURE_POINTS.KUNDALI_FORM_CONTACT,
      consent_marketing: true,
    });
  } catch {
    /* soft-fail: never surface a capture error on the chat value path */
  }
}
